import * as React from 'react'
import {ReactElement} from 'react'
import {Box, CardActionArea} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {Colors, FontSize, ResidentScrollBoundary} from '../../../../styledComponents/Constants'
import {BlockSpacer, currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import {forwardArrow, PlainLink} from '../../../../styledComponents/Links'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import styled from 'styled-components'
import {
    BuildOutlined,
    CardTravel,
    ContactPhoneOutlined,
    DirectionsBikeOutlined,
    DriveEtaOutlined,
    PetsOutlined,
    TwoWheelerOutlined,
    VideocamOutlined,
    WeekendOutlined,
} from '@mui/icons-material'
import {ApplyCategoryData} from '../repository/NatGetApplicationCategory'
import {APPLY_CATEGORY, RESIDENT_APPLY_DIVISION} from '../../../../CodeMaster'
import ScrollableCategorySelector from '../../../../components/shared/ScrollableCategorySelector'
import {withScrollToTop} from '../../../../components/shared/hoc/withScrollToTop'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {Location} from 'react-router-dom'

const KEY_APPLY_DIVISION_FILTER_CONDITIONS = 'NatResidentApplicationPage.filterConditions'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface State {
    applyCategories: ApplyCategoryData[]
    applyDivisions: RESIDENT_APPLY_DIVISION[]
    filterConditions: FilterConditions
    isLoadCompleted: boolean
}

interface FilterConditions {
    applyDivisions: RESIDENT_APPLY_DIVISION[]
}

class NatResidentApplicationPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applyCategories: [],
            applyDivisions: [],
            filterConditions: {
                applyDivisions: [],
            },
            isLoadCompleted: false,
        }

        this.handleChangeCategory = this.handleChangeCategory.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const applyCategoryResponse = await this.getApplyCategory()
            if (applyCategoryResponse) {
                const applyDivisions = this.extractApplyDivisions(applyCategoryResponse)
                this.setState({
                    filterConditions: this.getResidentApplyFilterConditions(applyDivisions),
                    applyCategories: applyCategoryResponse,
                    applyDivisions: applyDivisions,
                })
            }
        } finally {
            await eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    setResidentApplyFilterConditions(applyDivisions: RESIDENT_APPLY_DIVISION[]): void {
        const filterConditions = applyDivisions.map(applyDivision => {
            return applyDivision.value
        })

        SessionStorageHelper.setItem(
            KEY_APPLY_DIVISION_FILTER_CONDITIONS,
            JSON.stringify(filterConditions),
        )
    }

    getResidentApplyFilterConditions(applyDivisions: RESIDENT_APPLY_DIVISION[]): FilterConditions {
        const json: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_APPLY_DIVISION_FILTER_CONDITIONS)

        if (json) {
            const filterConditionsForJson: string[] = JSON.parse(json)
            return {
                applyDivisions: filterConditionsForJson
                    .filter(conditionApplyDivision =>
                        applyDivisions.includes(RESIDENT_APPLY_DIVISION.codeToEnumConstants(conditionApplyDivision)))
                    .map(conditionApplyDivision =>
                        RESIDENT_APPLY_DIVISION.codeToEnumConstants(conditionApplyDivision)),
            }
        }
        return {applyDivisions: []}
    }

    extractApplyDivisions(applyCategories: ApplyCategoryData[]): RESIDENT_APPLY_DIVISION[] {
        const categorySet: Set<RESIDENT_APPLY_DIVISION> = new Set<RESIDENT_APPLY_DIVISION>()
        applyCategories.forEach(applyCategory => {
            categorySet.add(RESIDENT_APPLY_DIVISION.codeToEnumConstants(applyCategory.applyDivision))
        })
        return Array.from(categorySet)
            .sort((a, b) => a.displayOrder - b.displayOrder)
    }

    async getApplyCategory(): Promise<Nullable<ApplyCategoryData[]>> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplicationCategory.bind(applicationRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
        return response?.details ?? null
    }

    getApplyCategoryIcon(applyCategory: string): ReactElement {
        switch (applyCategory) {
            case APPLY_CATEGORY.CONTACT_ADDRESS_ON_EMERGENCY.value:
                return (<ContactPhoneOutlinedIcon/>)
            case APPLY_CATEGORY.PARKING_USE_APPLY.value:
            case APPLY_CATEGORY.PARKING_USE_CANCEL.value:
            case APPLY_CATEGORY.CAR_CHANGE.value:
                return (<DriveEtaOutlinedIcon/>)
            case APPLY_CATEGORY.BICYCLE_PARKING_USE_APPLY.value:
            case APPLY_CATEGORY.BICYCLE_PARKING_USE_ADMIT_CANCEL.value:
                return (<DirectionsBikeOutlinedIcon/>)
            case APPLY_CATEGORY.BIKE_PARKING_USE_APPLY.value:
            case APPLY_CATEGORY.BIKE_PARKING_USE_ADMIT_CANCEL.value:
            case APPLY_CATEGORY.BIKE_CHANGE.value:
                return (<TwoWheelerOutlinedIcon/>)
            case APPLY_CATEGORY.BREEDING_PETS_APPLY.value:
            case APPLY_CATEGORY.MEDICAL_EXAMINATION_REPORT.value:
            case APPLY_CATEGORY.BREEDING_PETS_CANCEL.value:
                return (<PetsOutlinedIcon/>)
            case APPLY_CATEGORY.CONSTRUCTION_APPLY.value:
            case APPLY_CATEGORY.CONSTRUCTION_COMPLETION.value:
            case APPLY_CATEGORY.WINDOW_FILM_ATTACH_APPLY.value:
                return (<BuildOutlinedIcon/>)
            case APPLY_CATEGORY.LONG_ABSENCE.value:
                return (<CardTravelIcon/>)
            case APPLY_CATEGORY.HEAVY_GOODS_APPLY.value:
                return (<WeekendOutlinedIcon/>)
            case APPLY_CATEGORY.INSPECTION_APPLY.value:
                return (<VideocamOutlinedIcon/>)
            default:
                return (<></>)
        }
    }

    handleChangeCategory(selectedCategory: RESIDENT_APPLY_DIVISION[]): void {
        const {filterConditions} = this.state
        filterConditions.applyDivisions = selectedCategory
        this.setResidentApplyFilterConditions(selectedCategory)
        this.setState({
            filterConditions: filterConditions,
        })
    }

    handleApplyCategoryMenu(applyCategory: string): void {
        const {condominiumCd, navigate} = this.props
        let url: string = ''

        switch (applyCategory) {
            case APPLY_CATEGORY.CONTACT_ADDRESS_ON_EMERGENCY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/contactAddressOnEmergencies/agreement`
                break
            case APPLY_CATEGORY.PARKING_USE_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/parkingUses/agreement`
                break
            case APPLY_CATEGORY.PARKING_USE_CANCEL.value:
                url = `/resident/condominiums/${condominiumCd}/applications/parkingUsesWithdrawal/input`
                break
            case APPLY_CATEGORY.CAR_CHANGE.value:
                url = `/resident/condominiums/${condominiumCd}/applications/parkingUsesChange/agreement`
                break
            case APPLY_CATEGORY.BICYCLE_PARKING_USE_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/bicycleParkingUses/agreement`
                break
            case APPLY_CATEGORY.BICYCLE_PARKING_USE_ADMIT_CANCEL.value:
                url = `/resident/condominiums/${condominiumCd}/applications/bicycleParkingUsesWithdrawal/input`
                break
            case APPLY_CATEGORY.BIKE_PARKING_USE_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/bikeParkingUses/agreement`
                break
            case APPLY_CATEGORY.BIKE_PARKING_USE_ADMIT_CANCEL.value:
                url = `/resident/condominiums/${condominiumCd}/applications/bikeParkingUsesWithdrawal/input`
                break
            case APPLY_CATEGORY.BIKE_CHANGE.value:
                url = `/resident/condominiums/${condominiumCd}/applications/bikeParkingUsesChange/agreement`
                break
            case APPLY_CATEGORY.BREEDING_PETS_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/petBreeding/agreement`
                break
            case APPLY_CATEGORY.BREEDING_PETS_CANCEL.value:
                url = `/resident/condominiums/${condominiumCd}/applications/petBreedingStop/input`
                break
            case APPLY_CATEGORY.MEDICAL_EXAMINATION_REPORT.value:
                url = `/resident/condominiums/${condominiumCd}/applications/petMedicalCheckUpReport/agreement`
                break
            case APPLY_CATEGORY.CONSTRUCTION_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/constructions/agreement`
                break
            case APPLY_CATEGORY.CONSTRUCTION_COMPLETION.value:
                url = `/resident/condominiums/${condominiumCd}/applications/constructionComplete/input`
                break
            case APPLY_CATEGORY.WINDOW_FILM_ATTACH_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/windowFilmAttaches/agreement`
                break
            case APPLY_CATEGORY.LONG_ABSENCE.value:
                url = `/resident/condominiums/${condominiumCd}/applications/longAbsence/input`
                break
            case APPLY_CATEGORY.INSPECTION_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/browses/agreement`
                break
            case APPLY_CATEGORY.HEAVY_GOODS_APPLY.value:
                url = `/resident/condominiums/${condominiumCd}/applications/heavyLoadsCarries/agreement`
                break
        }
        navigate(url)
    }

    renderApplyFilter(): ReactElement {
        const {filterConditions, applyDivisions} = this.state
        const categories = filterConditions.applyDivisions

        return (
            <>
                <FilterContainer>
                    <ScrollableCategorySelector
                        onChangeCategory={this.handleChangeCategory}
                        categoryList={applyDivisions}
                        selectedCategoryList={categories}
                        displayAllButton
                    />
                </FilterContainer>
            </>
        )
    }

    renderApplyMenu(applyCategories: ApplyCategoryData[]): ReactElement {
        const {filterConditions} = this.state

        const filteredApplyInfos = filterConditions.applyDivisions.length > 0 ?
            applyCategories
                .filter((applyCategory) => {
                    const residentApplyDivision =
                        RESIDENT_APPLY_DIVISION.codeToEnumConstants(applyCategory.applyDivision)
                    return filterConditions.applyDivisions.includes(residentApplyDivision)
                }) :
            applyCategories

        return (
            <>
                {
                    filteredApplyInfos
                        .map((applyCategory) =>
                            <ApplyMenu key={applyCategory.applyCategory} onClick={() => {
                                this.handleApplyCategoryMenu(applyCategory.applyCategory)
                            }}>
                                <MenuContainer>
                                    {this.getApplyCategoryIcon(applyCategory.applyCategory)}
                                    <MenuItemName>{applyCategory.applyName}</MenuItemName>
                                </MenuContainer>
                            </ApplyMenu>,
                        )}
            </>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    届出・申請
                </PageTitle>
                <Box height={'15px'}/>
                <Box>
                    <ContentBody>
                        管理組合もしくは管理会社に届出や申請を行うことができます。本メニューにない手続きに関しましては、管理事務室にご確認ください。
                    </ContentBody>
                    <HistoryLinkBody>
                        <Box height={'15px'}/>
                        <ApplyHistoryLink
                            to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                            申請履歴を見る
                        </ApplyHistoryLink>
                    </HistoryLinkBody>
                    <Box height={'20px'}/>
                    {this.renderApplyFilter()}
                    <Box height={'15px'}/>
                    {this.renderApplyMenu(this.state.applyCategories)}
                    <Box height={'13px'}/>
                </Box>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(withScrollToTop(NatResidentApplicationPage, ResidentScrollBoundary))

const ContentBody = styled.div`
  max-width: 738px;
  margin: auto;
`
const HistoryLinkBody = styled.div`
  max-width: 738px;
  margin: auto;
`

const ApplyHistoryLink = styled(PlainLink)`
  display: block;
  text-align: right;
  height: 24px;
  margin: auto;
`

const FilterContainer = styled.div`
  background-color: ${Colors.Surface};
  width: calc(100% + 30px);
  margin: 0 -15px;
`

const ApplyMenu = styled(CardActionArea)`
  &.MuiCardActionArea-root {
    width: 100%;
    max-width: 738px;
    height: 60px;
    color: ${Colors.Body};
    background-color: ${Colors.Surface};
    line-height: 24px;
    padding: 18px 0;
    margin: 0 auto 2px auto;
    ${currentMedia.lessThan('sp')`
      width: calc(100% + 30px);
      margin: 0 -15px;
  `};
  }
`

const MenuContainer = styled.div`
  display: flex;
  margin: 0 15px;
  align-items: center;
`

const MenuItemName = styled.div`
  flex-grow: 1;
  text-align: start;
  position: relative;
  margin-left: 14px;

  :after {
    ${forwardArrow};
    border-top: solid 2px ${Colors.Body};
    border-right: solid 2px ${Colors.Body};
  }
`

const ContactPhoneOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(ContactPhoneOutlined)

const DriveEtaOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(DriveEtaOutlined)

const DirectionsBikeOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(DirectionsBikeOutlined)

const TwoWheelerOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(TwoWheelerOutlined)

const PetsOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(PetsOutlined)

const BuildOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(BuildOutlined)

const CardTravelIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(CardTravel)

const WeekendOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(WeekendOutlined)

const VideocamOutlinedIcon = withStyles({
    root: {
        fontSize: FontSize.Big,
        stroke: Colors.Surface,
        strokeWidth: 0.5,
        color: '#1C1B1F',
    },
})(VideocamOutlined)
